import {
  isProblemType,
  traceDataText,
  traceSubject,
  typeMatches,
  type SnapshotEntity,
  type SnapshotFilter,
  type TraceEvent,
  type WorldSnapshot,
} from '@ai-studio/shared';

import type { LiveTree } from '../engine/prism.js';

/**
 * Pure helpers of the Play tab's Game console and its server sync: row
 * text/colour, filters, the client ring buffer and upload batching.
 */

/** A trace event in the editor, with a local key for list rendering. */
export interface TraceRow extends TraceEvent {
  readonly key: number;
}

/** Colour family of a type (`physics.trigger.enter` → `physics`; problems → `error` / `warn`). */
export function traceCategory(type: string): string {
  if (/(^|\.)(error|panic)$/.test(type)) return 'error';
  if (/(^|\.)warn$/.test(type) || type === 'perf.spike') return 'warn';
  const head = type.split('.')[0] ?? type;
  return head || 'other';
}

/** Category chips, in a stable order: known families first, then the rest alphabetically. */
const CATEGORY_ORDER = [
  'error',
  'warn',
  'scene',
  'entity',
  'component',
  'transform',
  'physics',
  'input',
  'script',
  'game',
  'timer',
  'animation',
  'audio',
  'ui',
  'camera',
  'sequence',
  'particles',
  'net',
  'engine',
  'perf',
  'play',
  'trace',
];

export function categoryCounts(rows: readonly TraceEvent[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const c = traceCategory(row.type);
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  const rank = (c: string) => {
    const i = CATEGORY_ORDER.indexOf(c);
    return i < 0 ? CATEGORY_ORDER.length : i;
  };
  return [...counts.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
}

export type RowLevel = 'all' | 'problems' | 'errors';

export interface RowFilter {
  /** Hidden categories (chips switched off). */
  readonly hidden?: ReadonlySet<string>;
  /** Type prefixes typed by the user ("physics.trigger"). */
  readonly types?: readonly string[];
  readonly entity?: string;
  readonly text?: string;
  readonly level?: RowLevel;
}

/** Visible columns of one row. */
export interface RowText {
  readonly time: string;
  readonly frame: string;
  readonly type: string;
  readonly subject: string;
  readonly data: string;
  readonly category: string;
}

export function rowText(event: TraceEvent): RowText {
  return {
    time: event.t.toFixed(2),
    frame: String(event.f),
    type: event.type,
    subject: traceSubject(event),
    data: traceDataText(event, 240),
    category: traceCategory(event.type),
  };
}

export function rowMatches(event: TraceEvent, filter: RowFilter): boolean {
  const category = traceCategory(event.type);
  if (filter.hidden?.has(category)) return false;
  if (filter.level === 'errors' && category !== 'error') return false;
  if (filter.level === 'problems' && !isProblemType(event.type)) return false;
  if (filter.types && filter.types.length && !typeMatches(event.type, filter.types)) return false;
  if (filter.entity) {
    const needle = filter.entity.toLowerCase();
    const subject = traceSubject(event).toLowerCase();
    if (!subject.includes(needle) && !(event.entity ?? '').toLowerCase().includes(needle)) return false;
  }
  if (filter.text) {
    const r = rowText(event);
    const hay = `${r.type} ${r.subject} ${r.data}`.toLowerCase();
    if (!hay.includes(filter.text.toLowerCase())) return false;
  }
  return true;
}

export function filterRows<T extends TraceEvent>(rows: readonly T[], filter: RowFilter): T[] {
  return rows.filter((row) => rowMatches(row, filter));
}

/** "physics.trigger, input" → ["physics.trigger", "input"]. */
export function parseTypeList(text: string): string[] {
  return text
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** One row as pretty JSON (Copy as JSON). */
export function rowJson(event: TraceEvent | readonly TraceEvent[]): string {
  const strip = (e: TraceEvent) => {
    const { key: _key, ...rest } = e as TraceRow;
    return rest;
  };
  return JSON.stringify(Array.isArray(event) ? (event as readonly TraceEvent[]).map(strip) : strip(event as TraceEvent), null, 2);
}

/** Append to a ring buffer of at most `max` rows (new array, oldest dropped). */
export function pushRing<T>(rows: readonly T[], add: readonly T[], max: number): T[] {
  if (add.length === 0) return rows as T[];
  const next = rows.length + add.length > max ? [...rows.slice(Math.max(0, rows.length + add.length - max)), ...add] : [...rows, ...add];
  return next.length > max ? next.slice(next.length - max) : next;
}

/**
 * Bound the upload queue: past `cap`, drop the oldest ordinary events but
 * keep every error/warning (they are what the assistant needs most).
 */
export function trimOutbox<T extends TraceEvent>(outbox: readonly T[], cap: number): { kept: T[]; dropped: number } {
  if (outbox.length <= cap) return { kept: [...outbox], dropped: 0 };
  let excess = outbox.length - cap;
  const kept: T[] = [];
  let dropped = 0;
  for (const event of outbox) {
    if (excess > 0 && !isProblemType(event.type)) {
      excess -= 1;
      dropped += 1;
      continue;
    }
    kept.push(event);
  }
  return { kept, dropped };
}

/** Wire form of an event: null entity/name omitted to keep batches small. */
export function wireEvent(event: TraceEvent): Record<string, unknown> {
  const out: Record<string, unknown> = { seq: event.seq, f: event.f, t: event.t, type: event.type };
  if (event.entity !== null) out.entity = event.entity;
  if (event.name !== null) out.name = event.name;
  if (event.data && Object.keys(event.data).length) out.data = event.data;
  return out;
}

/* ------------------------------------------------------------------ */
/* World snapshot fallback for engine bundles without worldSnapshot     */
/* ------------------------------------------------------------------ */

/**
 * Build a {@link WorldSnapshot} from the older live-inspector bindings
 * (`liveTree` + `liveEntity`), applying the same filter as the engine would.
 */
export function snapshotFromLive(
  tree: LiveTree,
  entityOf: (id: string) => SnapshotEntity | null,
  filter: SnapshotFilter,
): WorldSnapshot {
  const limit = filter.limit ?? 100;
  const name = filter.name?.toLowerCase();
  const component = filter.component?.toLowerCase();
  const out: SnapshotEntity[] = [];
  let total = 0;
  for (const e of tree.entities) {
    if (name && !`${e.id} ${e.name}`.toLowerCase().includes(name)) continue;
    if (component && !e.components.some((c) => c.toLowerCase() === component)) continue;
    const full = out.length < limit || filter.near ? entityOf(e.id) : null;
    if (filter.near) {
      const p = full?.transform?.position;
      if (!p || p.length < 3) continue;
      const [x, y, z] = filter.near;
      const d = Math.hypot((p[0] ?? 0) - x, (p[1] ?? 0) - y, (p[2] ?? 0) - z);
      if (d > (filter.radius ?? 10)) continue;
    }
    total += 1;
    if (out.length >= limit) continue;
    out.push({
      id: e.id,
      name: e.name,
      parent: e.parent,
      ...(full?.transform ? { transform: full.transform } : {}),
      components: Object.fromEntries(e.components.map((c) => [c, full?.components?.[c] ?? null])),
    });
  }
  return { entities: out, frame: tree.frame, time: tree.time, total };
}
